import type { TrackSegment } from "../api/types";
import { candidateIDs, type ResolvedTrack, type TrackResolver } from "../playback/blocks";
import { artworkURL } from "./appleMusic";
import { bestMatch, coreTitle } from "./trackMatcher";

/** What resolving needs from Apple Music; `AppleMusic` is the real one. */
export interface AppleCatalog {
  songsByIDs(ids: string[]): Promise<MusicKit.Song[]>;
  librarySong(id: string): Promise<MusicKit.Song | null>;
  songsByISRC(isrc: string): Promise<MusicKit.Song[]>;
  searchSongs(term: string): Promise<MusicKit.Song[]>;
}

/**
 * Finds the recording a segment names in this listener's storefront, the way the
 * phone's `MusicKitService.resolvePlayableTrack` does. A catalog ID is only valid in
 * the storefront it came from, so it is one attempt of several: the ID and its
 * fallbacks → the listener's library → the ISRC → a scored search that re-finds the
 * same song and never substitutes another. Nothing the storefront will not play counts.
 */
export function appleTrackResolver(catalog: AppleCatalog): TrackResolver {
  return async (segment: TrackSegment) => {
    if (segment.music_provider === "soundcloud") {
      // Playable or not only at stream time; the engine skips it then.
      return {
        playableID: segment.provider_track_id,
        title: segment.title,
        artist: segment.artist,
        duration: segment.duration,
        artworkURL: segment.artwork_url,
      };
    }
    const ids = candidateIDs(segment);

    const byID = await catalog.songsByIDs(ids);
    for (const id of ids) {
      const song = byID.find((candidate) => candidate.id === id && isPlayable(candidate));
      if (song) return resolved(song);
    }

    for (const id of ids.filter((candidate) => candidate.startsWith("i."))) {
      const song = await catalog.librarySong(id);
      const catalogID = song?.attributes?.playParams?.catalogId;
      if (song && catalogID) return { ...resolved(song), playableID: catalogID };
    }

    if (segment.isrc) {
      const song = (await catalog.songsByISRC(segment.isrc)).find(isPlayable);
      if (song) return resolved(song);
    }

    const wantedTitle = coreTitle(segment.title);
    const sameSong = (await catalog.searchSongs(`${segment.title} ${segment.artist}`)).filter(
      (song) => coreTitle(song.attributes?.name ?? "") === wantedTitle,
    );
    const match = bestMatch(
      sameSong.map((song) => ({
        id: song.id,
        isrc: song.attributes?.isrc ?? null,
        title: song.attributes?.name ?? "",
        artist: song.attributes?.artistName ?? "",
        duration: (song.attributes?.durationInMillis ?? 0) / 1000,
        isPlayable: isPlayable(song),
      })),
      segment,
    );
    const song = match && sameSong.find((candidate) => candidate.id === match.id);
    return song ? resolved(song) : null;
  };
}

function isPlayable(song: MusicKit.Song): boolean {
  return Boolean(song.attributes?.playParams);
}

function resolved(song: MusicKit.Song): ResolvedTrack {
  const attributes = song.attributes;
  return {
    playableID: song.id,
    title: attributes?.name ?? "",
    artist: attributes?.artistName ?? "",
    duration: (attributes?.durationInMillis ?? 0) / 1000,
    artworkURL: attributes?.artwork ? artworkURL(attributes.artwork.url, 600) : null,
  };
}
